import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { UPDATE_LOST_FOUND_ITEM } from '../graphql/lost-found-queries';
import { toast } from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';
import SafeImage from './SafeImage';

const CATEGORY_LABELS = {
  LOST_ITEM_FOUND: 'Lost Item Found',
  REPORTED_LOST_ITEM: 'Reported Lost Item',
};

const STATUS_LABELS = {
  PENDING: 'Open',
  FOUND: 'Found',
  RETURNED: 'Returned',
  DISCARDED: 'Discarded',
  SHIPPED: 'Shipped',
};

const STATUS_CLASSES = {
  PENDING: 'bg-yellow-100 text-yellow-800',
  FOUND: 'bg-blue-100 text-blue-800',
  RETURNED: 'bg-green-100 text-green-800',
  DISCARDED: 'bg-red-100 text-red-800',
  SHIPPED: 'bg-emerald-100 text-emerald-800',
};

const LostFoundItem = ({ item, onStatusChange }) => {
  const [updateStatus] = useMutation(UPDATE_LOST_FOUND_ITEM);
  
  // Lightbox states
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  const handleStatusChange = async (newStatus) => {
    try {
      await updateStatus({
        variables: {
          input: {
            id: item.id,
            status: newStatus
          }
        }
      });
      
      toast.success(`Item marked as ${newStatus.toLowerCase()}`);
      if (onStatusChange) {
        onStatusChange(item.id, newStatus);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update item status');
    }
  };

  // Truncate description to 30 words
  const truncateDescription = (text, wordCount = 30) => {
    const words = text.split(' ');
    if (words.length <= wordCount) return text;
    return words.slice(0, wordCount).join(' ') + '...';
  };

  const formattedDate = formatDistanceToNow(new Date(item.createdAt), { addSuffix: true });

  const handleOpenLightbox = (index) => {
    setCurrentPhotoIndex(index);
    setLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    setLightboxOpen(false);
  };

  const handlePrevPhoto = (e) => {
    e.stopPropagation();
    if (item.photos && item.photos.length > 0) {
      setCurrentPhotoIndex((prevIndex) => 
        prevIndex === 0 ? item.photos.length - 1 : prevIndex - 1
      );
    }
  };

  const handleNextPhoto = (e) => {
    e.stopPropagation();
    if (item.photos && item.photos.length > 0) {
      setCurrentPhotoIndex((prevIndex) => 
        prevIndex === item.photos.length - 1 ? 0 : prevIndex + 1
      );
    }
  };

  // Add event listener for keyboard navigation
  useEffect(() => {
    // Handle keyboard navigation
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      
      switch (e.key) {
        case 'ArrowLeft':
          handlePrevPhoto(e);
          break;
        case 'ArrowRight':
          handleNextPhoto(e);
          break;
        case 'Escape':
          handleCloseLightbox();
          break;
        default:
          break;
      }
    };

    if (lightboxOpen) {
      window.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when lightbox is open using React-friendly approach
      document.body.style.overflow = 'hidden';
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      // Always restore body scroll on cleanup
      document.body.style.overflow = 'auto';
    };
  }, [lightboxOpen, item.photos, handlePrevPhoto, handleNextPhoto, handleCloseLightbox]);

  return (
    <>
      <div className="bg-white rounded-lg shadow-md border border-gray-200 transition duration-150 ease-in-out hover:shadow-lg border-l-4 border-l-orange-500">
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-6 gap-6 mb-6">
            {/* Info Section */}
            <div className="col-span-1 lg:col-span-3">
              <div className="flex items-center gap-2 mb-1">
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                  {CATEGORY_LABELS[item.category] || item.category || 'Lost Item Found'}
                </span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">
                {truncateDescription(item.description, 5)}
              </h3>
              <p className="text-base text-gray-700 mb-2">
                {item.property?.name || 'Unknown Property'}
              </p>
              <p className="text-sm text-gray-500 mb-1">{formattedDate}</p>
              <div className="flex items-center gap-1">
                <span className="text-sm text-gray-700">Submitted by</span>
                {item.isGuestSubmitted ? (
                  <span className="flex items-center gap-1.5">
                    <span className="text-sm text-gray-900 font-medium">{item.guestName || 'Guest'}</span>
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                      Guest
                    </span>
                  </span>
                ) : (
                  <span className="text-sm text-gray-900 font-medium">
                    {item.submitter ? `${item.submitter.firstName} ${item.submitter.lastName}` : 'Unknown'}
                  </span>
                )}
              </div>
            </div>

            {/* Photo Section */}
            <div className="col-span-1 lg:col-span-2">
              {item.photos && item.photos.length > 0 ? (
                <div>
                  <p className="text-sm font-medium text-gray-900 mb-2">
                    Submitted Photo{item.photos.length > 1 ? `s (${item.photos.length})` : ''}
                  </p>
                  <div 
                    className="group relative w-[120px] h-[120px] bg-gray-100 rounded-lg overflow-hidden shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => handleOpenLightbox(0)}
                  >
                    <SafeImage 
                      src={item.photos[0]} 
                      alt="Lost item" 
                      className="w-full h-full object-cover" 
                      lazy={false}
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-opacity flex items-center justify-center">
                      <span className="text-white opacity-0 group-hover:opacity-100 text-xs font-medium transition-opacity">
                        Click to enlarge
                      </span>
                    </div>
                    {item.photos.length > 1 && (
                      <div className="absolute top-2 right-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
                        +{item.photos.length - 1}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="w-[120px] h-[120px] flex items-center justify-center bg-gray-100 rounded-lg">
                  <span className="text-gray-400 text-xs">No photo</span>
                </div>
              )}
            </div>

            {/* Status Section */}
            <div className="col-span-1 lg:col-span-1 flex justify-end">
              <span className={`inline-flex items-center h-12 px-4 rounded-full text-sm font-medium ${STATUS_CLASSES[item.status] || STATUS_CLASSES.PENDING}`}>
                {STATUS_LABELS[item.status] || item.status}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3">
            <Link
              to={`/dashboard/lost-found/${item.id}`}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              View Details
            </Link>
            
            {item.status === 'PENDING' && (
              <>
                <button
                  onClick={() => handleStatusChange('FOUND')}
                  className="inline-flex items-center px-3 py-2 border border-blue-300 rounded-md text-sm font-medium bg-blue-50 text-blue-800 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Mark Found
                </button>
                
                <button
                  onClick={() => handleStatusChange('DISCARDED')}
                  className="inline-flex items-center px-3 py-2 border border-red-300 rounded-md text-sm font-medium bg-red-50 text-red-800 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Discarded
                </button>
              </>
            )}
            {item.status === 'FOUND' && (
              <button
                onClick={() => handleStatusChange('DISCARDED')}
                className="inline-flex items-center px-3 py-2 border border-red-300 rounded-md text-sm font-medium bg-red-50 text-red-800 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Discarded
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Lightbox Modal */}
      {lightboxOpen && item.photos && item.photos.length > 0 && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center p-4"
          onClick={handleCloseLightbox}
        >
          <div className="relative max-w-4xl w-full max-h-full">
            {/* Close button */}
            <button 
              className="absolute right-4 top-4 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-70 transition-all z-10"
              onClick={handleCloseLightbox}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            {/* Image */}
            <div className="relative">
              <SafeImage 
                src={item.photos[currentPhotoIndex]} 
                alt={`Lost item photo ${currentPhotoIndex + 1}`} 
                className="max-h-[80vh] mx-auto object-contain"
                lazy={false}
              />
              
              {/* Image counter */}
              <div className="absolute bottom-4 left-0 right-0 text-center text-white bg-black bg-opacity-50 py-1">
                {currentPhotoIndex + 1} / {item.photos.length}
              </div>
            </div>
            
            {/* Navigation buttons */}
            {item.photos.length > 1 && (
              <>
                <button 
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-70 transition-all"
                  onClick={handlePrevPhoto}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button 
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-70 transition-all"
                  onClick={handleNextPhoto}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default LostFoundItem; 
